import { useEffect, useId, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  PERSON_SELECT_NOT_LISTED,
  Toast,
} from '@wundo/ui';
import type { CurrentUser } from '@wundo/client/auth.ts';
import { useImpersonatedBy } from '@wundo/client/impersonation.ts';
import { fetchOneOnOneColleagues } from '@/lib/colleagues.ts';
import { firstName } from '@/lib/firstName.ts';
import {
  managerQuestionPostponed,
  postponeManagerQuestion,
} from '@/lib/managerQuestionPostponed.ts';
import { m } from '@/paraglide/messages.js';
import { ManagerSelect } from './ManagerSelect.tsx';
import { useManagerAnswer } from './useManagerAnswer.ts';

const TOAST_MS = 4000;

export interface ManagerQuestionDialogProps {
  user: CurrentUser | undefined;
}

/**
 * Asks once "who is your manager?", so a 1:1 with them is prepared from the
 * right side. Only for somebody with colleagues on the Team page and no manager
 * among them; "Later" asks again on the next visit.
 */
export function ManagerQuestionDialog({ user }: ManagerQuestionDialogProps) {
  const [postponed] = useState(managerQuestionPostponed);
  /*
   * An address with a request of its own (a form to open, a provider's
   * refusal) brings its own dialog, so the question waits for a plain visit.
   */
  const [plainArrival] = useState(() => window.location.search === '');
  /* Staff signed in as the user must not answer for them. */
  const impersonatedBy = useImpersonatedBy();
  /* Focus lands on the dialog itself, so the field shows no ring before anybody reaches it. */
  const popup = useRef<HTMLDivElement>(null);
  const [dismissed, setDismissed] = useState(false);
  const [choice, setChoice] = useState<string | null>(null);
  const [savedName, setSavedName] = useState<string | null>(null);
  const hintId = useId();
  const answer = useManagerAnswer();

  const eligible =
    plainArrival &&
    impersonatedBy === null &&
    !postponed &&
    user !== undefined &&
    user.manager_question_answered_at === null &&
    !user.demo;

  const { data: colleagues } = useQuery({
    queryKey: ['oneOnOneColleagues', user?.id, 'active'],
    queryFn: () => fetchOneOnOneColleagues(user!.id, { hidden: false }),
    enabled: eligible,
  });

  const asking =
    eligible &&
    colleagues !== undefined &&
    colleagues.length > 0 &&
    !colleagues.some((colleague) => colleague.relationship === 'manager');

  useEffect(() => {
    if (savedName === null) return;
    const timer = window.setTimeout(() => setSavedName(null), TOAST_MS);
    return () => window.clearTimeout(timer);
  }, [savedName]);

  const later = () => {
    postponeManagerQuestion();
    setDismissed(true);
  };

  const save = () => {
    if (!user || choice === null) return;
    const chosen = colleagues?.find((colleague) => colleague.id === choice);

    answer.mutate(
      { userId: user.id, choice },
      {
        onSuccess: () => {
          setDismissed(true);
          if (chosen) setSavedName(firstName(chosen.label));
        },
      },
    );
  };

  return (
    <>
      <Dialog
        open={asking && !dismissed}
        onOpenChange={(open) => !open && !answer.isPending && later()}
      >
        <DialogContent size='md' ref={popup} initialFocus={popup}>
          <DialogHeader>
            <DialogTitle>{m.manager_question_title()}</DialogTitle>
          </DialogHeader>
          <div className='flex flex-col gap-3 px-5 py-4'>
            <DialogDescription className='text-ink-body'>
              {m.manager_question_explanation()}
            </DialogDescription>
            <ManagerSelect
              colleagues={colleagues ?? []}
              value={choice}
              onValueChange={setChoice}
              disabled={answer.isPending}
              aria-describedby={hintId}
            />
            {choice === PERSON_SELECT_NOT_LISTED && (
              <p className='text-sm text-ink-body'>
                {m.manager_question_not_listed_note()}
              </p>
            )}
            {answer.isError && (
              <p role='alert' className='text-sm text-danger'>
                {m.manager_question_failed()}
              </p>
            )}
            <p id={hintId} className='text-xs text-ink-muted'>
              {m.manager_question_hint()}
            </p>
          </div>
          <DialogFooter className='pt-1'>
            <Button variant='quiet' size='sm' disabled={answer.isPending} onClick={later}>
              {m.manager_question_later()}
            </Button>
            <Button
              size='sm'
              disabled={choice === null}
              loading={answer.isPending}
              onClick={save}
            >
              {m.manager_question_save()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Toast
        open={savedName !== null}
        message={savedName ? m.manager_question_saved({ name: savedName }) : ''}
      />
    </>
  );
}
